import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

export type VirtualTour = Awaited<ReturnType<typeof createVirtualTour>>;

type CreateVirtualTourOptions = {
  mutationConfig?: MutationConfig<typeof createVirtualTour>;
};

export type VirtualTourOptions = {
  queryConfig?: QueryConfig<typeof getVirtualTourById>;
  id: string;
  virtualTourId: string;
};

type CreateVirtualTourType = {
  name: string;
  estateId: string;
  // biome-ignore lint/suspicious/noExplicitAny: ignored
  data: any;
};

//* Get Virtual Tour by ID --- BEGIN
const getVirtualTourById = (estateId: string, virtualTourId: string) => {
  return extractData(
    api.estates({ id: estateId })['virtual-tour']({ virtualTourId: virtualTourId }).get()
  );
};

export const getVirtualTourByIdOptions = (id: string, virtualTourId: string) => ({
  queryKey: ['estateId', id, 'tourId', virtualTourId],
  queryFn: () => getVirtualTourById(id, virtualTourId),
});

const getVirtualTourByIdQueryOptions = (id: string, virtualTourId: string) => {
  return queryOptions(getVirtualTourByIdOptions(id, virtualTourId));
};

export const ensureVirtualTourByIdQueryData = ({
  context: { queryClient },
  params,
}: LoaderProps) => {
  return queryClient.ensureQueryData(
    getVirtualTourByIdQueryOptions(params.estateId, params.tourId)
  );
};

export const useGetVirtualTourById = ({ queryConfig, id, virtualTourId }: VirtualTourOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getVirtualTourByIdQueryOptions(id, virtualTourId) });
};
//* Get Virtual Tour by ID --- END

//* Get Virtual Tours by Estate ID --- BEGIN
export type VirtualTours = Awaited<ReturnType<typeof getVirtualToursByEstateId>>;

const getVirtualToursByEstateId = (estateId: string) => {
  return extractData(api.estates({ id: estateId })['virtual-tour'].all.get());
};

export const getVirtualToursByEstateIdOptions = (estateId: string) => ({
  queryKey: ['estateId', estateId],
  queryFn: () => getVirtualToursByEstateId(estateId),
});

export const ensureGetVirtualToursByEstateIdQueryData = ({
  context: { queryClient },
  params,
}: LoaderProps) => {
  return queryClient.ensureQueryData(getVirtualToursByEstateIdOptions(params.estateId));
};

export const useGetVirtualToursByEstateId = ({
  queryConfig,
  id,
}: {
  queryConfig?: QueryConfig<typeof getVirtualToursByEstateId>;
  id: string;
}) => {
  return useSuspenseQuery({ ...queryConfig, ...getVirtualToursByEstateIdOptions(id) });
};
//* Get Virtual Tours by Estate ID --- END

//* Create Virtual Tour --- BEGIN
const createVirtualTour = (body: CreateVirtualTourType) => {
  return extractData(api.estates({ id: body.estateId })['virtual-tour'].post(body));
};

export const useCreateVirtualTour = ({ mutationConfig }: CreateVirtualTourOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: (body: CreateVirtualTourType) => createVirtualTour(body),
  });
};
//* Create Virtual Tour --- BEGIN

//* Update Virtual Tour --- BEGIN
const updateVirtualTour = (
  body: CreateVirtualTourType,
  virtualTourId: string,
  estateId: string
) => {
  return extractData(api.estates({ id: estateId })['virtual-tour']({ virtualTourId }).patch(body));
};
export const useUpdateVirtualTour = ({
  mutationConfig,
  tourId,
  estateId,
}: {
  mutationConfig?: MutationConfig<typeof updateVirtualTour>;
  tourId: string;
  estateId: string;
}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: (body: CreateVirtualTourType) => updateVirtualTour(body, tourId, estateId),
  });
};
//* Update Virtual Tour --- END

//* Delete Virtual Tour --- BEGIN
const deleteVirtualTour = (estateId: string, virtualTourId: string) => {
  return extractData(api.estates({ id: estateId })['virtual-tour']({ virtualTourId }).delete());
};
export const useDeleteVirtualTour = ({
  mutationConfig,
  estateId,
  virtualTourId,
}: {
  mutationConfig?: MutationConfig<typeof deleteVirtualTour>;
  estateId: string;
  virtualTourId: string;
}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: () => deleteVirtualTour(estateId, virtualTourId),
  });
};
//* Delete Virtual Tour --- END

//* Share Virtual Tour --- BEGIN
const shareVirtualTour = ({
  estateId,
  virtualTourId,
  expiresIn,
}: {
  estateId: string;
  virtualTourId: string;
  expiresIn?: number;
}) => {
  return extractData(
    api.estates({ id: estateId })['virtual-tour']({ virtualTourId }).share.post({ expiresIn })
  );
};
export const useShareVirtualTour = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof shareVirtualTour>;
} = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: shareVirtualTour,
  });
};
//* Share Virtual Tour --- END

//* Revoke Virtual Tour --- BEGIN
const revokeToken = ({ token }: { token: string }) => {
  return extractData(api.token({ token })['virtual-tour'].delete());
};
export const useRevokeToken = ({
  mutationConfig,
}: { mutationConfig?: MutationConfig<typeof revokeToken> } = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: revokeToken,
  });
};
//* Revoke Virtual Tour --- END

//* Get Virtual Tour with token --- BEGIN
export const getVirtualTourByToken = (token: string) => {
  return extractData(api.validate.post({ token, type: 'virtual-tour' }));
};
export const useGetVirtualTourByToken = ({
  mutationConfig,
}: { mutationConfig?: MutationConfig<typeof getVirtualTourByToken> } = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: getVirtualTourByToken,
  });
};
//* Get Virtual Tour with token --- END

//* Update Virtual Tour Status --- BEGIN
const updateVirtualTourStatus = ({
  estateId,
  virtualTourId,
  publish,
}: {
  estateId: string;
  virtualTourId: string;
  publish: string;
}) => {
  return extractData(
    api.estates({ id: estateId })['virtual-tour']({ virtualTourId }).publish.patch({ publish })
  );
};

export const useUpdateVirtualTourStatus = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof updateVirtualTourStatus>;
} = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: updateVirtualTourStatus,
  });
};
//* Update Virtual Tour Status --- END
